import { forwardRef, useImperativeHandle, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useUpdateTemplateSettings } from "../../../graphql/hooks/invoiceInterpretationTemplates/useUpdateTemplateSettings";
import type { TemplateSettingsInput } from "../../../models/InvoiceInterpretationInputs";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { theme } from "../../../theme";
import Body from "../../atoms/typography/Body";
import BodySmall from "../../atoms/typography/BodySmall";
import TextField from "../../atoms/inputs/TextField";
import Divider from "../../atoms/Divider";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";
import { HorizontalSpacer } from "../../atoms/Spacers";
import FilterChip from "../../atoms/inputs/FilterChip";
import Alert from "../../molecules/Alert";
import { Row } from "react-grid-system";

interface Props {
  onToggleDisplayHighlights: () => void;
  templateSettings?: TemplateSettingsInput;
}

const ExampleContainer = styled.div`
  padding: 8px;
  border-radius: 4px;
  background-color: ${theme.colors.bgSubtle};
  width: fit-content;
`;

const EditSettings = forwardRef<any, Props>(
  ({ onToggleDisplayHighlights, templateSettings }, ref) => {
    const { t } = useTranslation();
    const { id: clientIdentifier, templateId } = useParams();

    const [settings, setSettings] = useState<TemplateSettingsInput>(
      templateSettings ?? {},
    );
    const [dirty, setDirty] = useState(false);
    const [showOverviewHighlights, setShowOverviewHighlights] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const { updateTemplateSettings, loading } = useUpdateTemplateSettings({
      onError: (e) => {
        setError(e?.message || t("errors.somethingWentWrong"));
      },
      onCompleted: (result) => {
        if (!result.invoiceInterpretation?.updateTemplateSettings?.success) {
          setError(t("errors.somethingWentWrong"));
        }
      },
    });

    useEffect(() => {
      if (templateSettings) setSettings(templateSettings);
    }, [templateSettings]);

    const save = async () => {
      if (!dirty) return;
      setError(null);

      await updateTemplateSettings({
        templateId: templateId!,
        clientIdentifier: clientIdentifier!,
        input: settings,
      });
      setDirty(false);
    };

    const handleNumberChange = (
      field: keyof TemplateSettingsInput,
      value: string,
    ) => {
      if (value === "") {
        setSettings((prev) => ({ ...prev, [field]: undefined }));
        setDirty(true);
        return;
      }

      const validNumberPattern = /^-?\d*\.?\d*$/;

      if (!validNumberPattern.test(value)) {
        return;
      }

      const parsed = Number(value);

      if (!isNaN(parsed)) {
        setSettings((prev) => ({ ...prev, [field]: parsed }));
        setDirty(true);
      }
    };

    const handleStringChange = (
      field: keyof TemplateSettingsInput,
      value: string,
    ) => {
      setSettings((prev) => ({ ...prev, [field]: value }));
      setDirty(true);
    };

    const onToggleOverviewHighlights = () => {
      setShowOverviewHighlights((prev) => !prev);
      onToggleDisplayHighlights();
    };

    useImperativeHandle(ref, () => ({
      save,
      isDirty: () => dirty,
    }));

    const onClickSave = async () => {
      try {
        await save();
      } catch (e: any) {
        setError(e.message || t("errors.somethingWentWrong"));
      }
    };

    return (
      <div>
        {error && <Alert type="error" title={error} />}

        <FilterChip
          titleKey={t("templates.displayHighlights")}
          onSelect={onToggleOverviewHighlights}
          selected={showOverviewHighlights}
        />

        <HorizontalSpacer spacing={2} />
        <Body>{t("templates.extraction")}</Body>
        <HorizontalSpacer spacing={1} />

        <TextField
          label={t("templates.dateFormat")}
          placeholder={t("templates.dateFormatExample")}
          value={settings.dateFormat ?? ""}
          onChange={(value: string) => handleStringChange("dateFormat", value)}
        />

        <HorizontalSpacer spacing={1} />
        <ExampleContainer>
          <BodySmall color={theme.colors.textSecondary}>
            {t("templates.example")}
          </BodySmall>
          <HorizontalSpacer spacing={1} />
          <div style={{ display: "flex", gap: 16 }}>
            <div style={{ width: 200 }}>
              <BodySmall>yyyy-MM-dd</BodySmall>
            </div>
            <div style={{ width: 200 }}>
              <BodySmall color={theme.colors.textSecondary}>
                2024-01-24
              </BodySmall>
            </div>
          </div>
          <HorizontalSpacer spacing={1} />
          <div style={{ display: "flex", gap: 16 }}>
            <div style={{ width: 200 }}>
              <BodySmall>d/MM-yy</BodySmall>
            </div>
            <div style={{ width: 200 }}>
              <BodySmall color={theme.colors.textSecondary}>7/11-24</BodySmall>
            </div>
          </div>
        </ExampleContainer>

        <HorizontalSpacer spacing={2} />
        <Divider />
        <HorizontalSpacer spacing={2} />

        <Body>{t("templates.advanced")}</Body>
        <HorizontalSpacer spacing={1} />

        <TextField
          label={t("templates.wordDistance")}
          placeholder="4.8"
          value={settings.withinLineMultiplier ?? ""}
          width="100px"
          onChange={(value: string) =>
            handleNumberChange("withinLineMultiplier", value)
          }
        />

        <HorizontalSpacer spacing={2} />
        <div style={{ display: "flex", gap: 32, alignItems: "flex-end" }}>
          <TextField
            label={t("templates.rowDistance")}
            placeholder="-22"
            value={settings.withinLineLowerBound ?? ""}
            width="100px"
            onChange={(value: string) =>
              handleNumberChange("withinLineLowerBound", value)
            }
          />
          <TextField
            placeholder="22"
            label=" "
            value={settings.withinLineUpperBound ?? ""}
            width="100px"
            onChange={(value: string) =>
              handleNumberChange("withinLineUpperBound", value)
            }
          />
        </div>

        <HorizontalSpacer spacing={2} />
        <Divider />
        <HorizontalSpacer spacing={2} />

        <Row nogutter justify="end">
          <OutlinedButton
            title={t("common.save")}
            color={theme.colors.primary}
            medium
            onClick={onClickSave}
            disabled={loading || !dirty}
          />
        </Row>
      </div>
    );
  },
);

export default EditSettings;
